<template>
    <stepper-layout
        :stepTitle="'Select Unit(s)'"
        :stepIcon="'business'"
        :class="darkMode ? '' : 'pl-1 pt-1'"
    >
        <v-card
            v-for="(unit, i) in balanceDetails" :key="i"
            style="outline: grey 1px solid"
            class="mb-4 mx-1"
        >
            <v-card-title color="grey lighten">
                <v-layout
                    text-h6
                    justify-space-between
                    align-center
                >
                    <v-layout column>
                        <span class="mt-1 text-h6">Unit {{ unit.this_month.unit_name }}</span>
                        <v-layout column v-if="canTakePayment(unit.this_month, i)">
                            <span class="text-body-1 mt-3">Select payment option...</span>
                            <v-layout align-center>
                                <v-radio-group row hide-details>
                                    <v-radio
                                        data-testid="payment-unit-this-month-radio"
                                        class="payment-unit-this-month-radio"
                                        v-if="unit.this_month.total_with_tax"
                                        :value="unit.this_month"
                                        :label="`This Month ($${parseFloat(unit.this_month.total_with_tax).toFixed(2)})`"
                                        @change="addItem(i, unit.this_month, 0)"
                                    />
                                    <v-radio
                                        data-testid="payment-unit-next-month-radio"
                                        class="payment-unit-next-month-radio"
                                        v-if="!unit.this_month.total_with_tax"
                                        :value="unit.next_month"
                                        :label="`Next Month ($${parseFloat(unit.next_month.total_with_tax).toFixed(2)})`"
                                        @change="addItem(i, unit.next_month, 1)"
                                    />
                                    <v-radio
                                        data-testid="payment-unit-next-month-radio"
                                        class="payment-unit-next-month-radio"
                                        v-if="unit.this_month.total_with_tax"
                                        :value="unit.next_month"
                                        :label="`This Month + Next Month ($${parseFloat(unit.next_month.total_with_tax).toFixed(2)})`"
                                        @change="addItem(i, unit.next_month, 1)"
                                    />
                                    <v-radio
                                        data-testid="payment-unit-remove-radio"
                                        class="payment-unit-remove-radio"
                                        :value="unit"
                                        label="Remove Payment"
                                        @change="removeItem(getUnitName(balanceDetails, i))"
                                    />
                                </v-radio-group>
                            </v-layout>
                        </v-layout>
                    </v-layout>
                </v-layout>
            </v-card-title>
            <v-divider />
            <v-layout column class="mt-4 mb-2 mx-4">
                <v-flex v-if="!unit.this_month.total_with_tax && !isFutureEligible(i)" class="subheading mb-3">
                    Tenant has paid in full for this month and next month on this unit. Store Space's call center only
                    supports paying the balances for the current and next month of a customer's unit.
                </v-flex>
                <v-flex v-if="!isPaymentEligible(i)" class="subheading mb-3">
                    Store Space's call center does not support online payment processing on accounts that are more than
                    {{ maxPastDueDays }} past due. This customer must pay in person at his/her facility.
                </v-flex>
                <v-layout v-else align-start>
                    <v-flex xs6>
                        <charge-breakdown
                            :chargePeriod="'This Month'"
                            :balance="unit.this_month"
                        />
                    </v-flex>
                    <v-flex xs6>
                        <charge-breakdown
                            :chargePeriod="unit.this_month.total_with_tax ? 'Next Month': 'This Month and Next Month'"
                            :balance="unit.next_month"
                        />
                    </v-flex>
                </v-layout>
            </v-layout>
        </v-card>
    </stepper-layout>
</template>

<script>
/* eslint-disable eqeqeq */
/* eslint-disable no-return-assign */
import ChargeBreakdown from "../partials/ChargeBreakdown";
import StepperLayout from "@/layouts/StepperLayout";
import { mapState } from "vuex";
import moment from "moment";

export default {
  components: { ChargeBreakdown, StepperLayout },

  methods: {
    addItem(id, unit, prepaid_months) {
      const ledger = this.ledgers.find(ledger => ledger.facility_unit.sitelink_id == id);
      let cartItem = {
        unit: unit.unit_name,
        total: unit.total_with_tax,
        unit_id: id,
        prepaid_months,
        ledger_id: ledger.pms_ledger_id
      };
      let paymentCart = [...this.cart];
      if (paymentCart.find(item => item.unit_id === id)) {
        paymentCart.splice(paymentCart.findIndex(cartItem => cartItem.unit_id == id), 1);
      }
      paymentCart.push(cartItem);
      let cartPayload = { key: "cart", value: paymentCart };
      this.$store.commit("updatePaymentValue", cartPayload);
    },

    removeItem(id) {
      const cartWithPaymentRemoved = this.cart.filter(item => item.unit !== id);
      const cartPayload = { key: "cart", value: cartWithPaymentRemoved };
      this.$store.commit("updatePaymentValue", cartPayload);
    },

    filterBalance(charges) {
      if (charges && charges.length) {
        return charges.filter(charge => charge.total);
      }
    },

    isPaymentEligible(unitId) {
      const today = moment.utc().format();
      const selectedUnit = this.ledgers.find(ledger => ledger.facility_unit.sitelink_id == unitId); // @todo sitelink
      const standardPastDueLimit = moment(today).subtract(this.salesPastDue, "days");
      const arPastDueLimit = moment(today).subtract(this.arPastDue, "days");
      const appliedPastDueLimit = this.$route.name === "arTenant" ? arPastDueLimit : standardPastDueLimit;

      if (selectedUnit && selectedUnit.paid_thru) {
        const paidThrough = moment.utc(selectedUnit.paid_thru).subtract(8, "hours");
        return paidThrough.isAfter(appliedPastDueLimit);
      }
    },

    isFutureEligible(unitId) {
      const today = moment.utc().format();
      const selectedUnit = this.ledgers.find(ledger => {
        return ledger.facility_unit.sitelink_id == unitId; // @todo sitelink
      });
      const futureLimit = moment(today).add(1, "M").endOf("M");
      if (selectedUnit && selectedUnit.paid_thru) {
        const paidThrough = moment.utc(selectedUnit.paid_thru).add(1, "days");
        return paidThrough.isSameOrBefore(futureLimit);
      }
    },

    totalBalance(arr) {
      if (typeof arr !== "undefined") {
        return arr.reduce((acc, currVal) => {
          return acc += parseFloat(currVal.dcTotal);
        }, 0);
      }
    },

    getUnitName(balanceDetails, i) {
      if (balanceDetails && balanceDetails[i].this_month) {
        return balanceDetails[i].this_month.unit_name;
      } else {
        return "";
      }
    },

    canTakePayment(unit, unitId) {
      if (unit && unitId && this.isPaymentEligible(unitId)) {
        return (unit.total_with_tax || this.isFutureEligible(unitId));
      } else {
        return false;
      }
    }
  },

  computed: {
    ...mapState({
      balanceDetails: state => state.tenant.payment.balanceDetails,
      cart: state => state.tenant.payment.cart,
      arPastDue: state => state.tenant.arPastDue,
      salesPastDue: state => state.tenant.salesPastDue,
      darkMode: state => state.application.settings.darkTheme,
      ledgers: state => state.tenant.ledgers
    }),
    maxPastDueDays() {
      if (this.$route.name === "arTenant") {
        return `${this.arPastDue} days`;
      } else {
        return `${this.salesPastDue} days`;
      }
    }
  }
};
</script>

<style scoped>

</style>
